import React, { useCallback, useEffect, useState } from 'react';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import styled from 'styled-components';
import FeedbackSectionTextarea from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackSectionTextarea';
import {
  FeedbackCommentProps,
  UserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { SelectedFeedback } from './utils';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  postQuestionLevelComment,
  postSectionLevelComment,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { useClient } from 'shared/apollo';
import { requestInit } from 'shared/auth/hooks/useRequestState';
import { SuccessResponse } from '../ReportsBeta/NewReport/api';

interface CommentAreaProps {
  defaultOpenState: boolean;
  details?: string;
  buttonText?: string;
  currentUserEmail: string | undefined;
  currentUserId: string | undefined;
  selectedSection: SelectedFeedback;
  reportId: number;
  userName: string;
  userType: UserType;
  onSuccess: (
    selected: SelectedFeedback,
    comment: FeedbackCommentProps,
  ) => unknown;
}

const StyledButton = styled(TrueImpactButton)`
  width: 100%;
  outline: inherit;
  margin-top: 20px;
  border: 1px dashed;
`;

const StyledDiv = styled.div`
  margin-top: 20px;
`;

const CommentArea: React.FC<CommentAreaProps> = ({
  details,
  defaultOpenState,
  currentUserEmail,
  currentUserId,
  buttonText = 'Leave a comment',
  userName,
  userType,
  reportId,
  selectedSection,
  onSuccess,
}) => {
  const client = useClient();
  const [open, setOpen] = useState<boolean>(defaultOpenState);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [requestState, makeRequest, setRequest] = useGraphQLRequest<
    SuccessResponse<'newComment'>
  >();
  const [comment, setComment] = useState<FeedbackCommentProps | undefined>(
    undefined,
  );

  const handleSave = useCallback(
    (c: string) => {
      setComment({
        commentId: 0,
        userId: Number(currentUserId),
        createdAt: new Date().toISOString(),
        name: userName,
        text: c,
        accountType: userType,
        email: currentUserEmail,
        details,
      });
      if (selectedSection.question?.questionId) {
        makeRequest(
          postQuestionLevelComment(
            client!,
            reportId,
            selectedSection.question.questionId,
            c,
            selectedSection.section.sectionId,
            details,
          ),
        );
      } else {
        makeRequest(
          postSectionLevelComment(
            client!,
            undefined,
            c,
            reportId,
            selectedSection.section.sectionId,
          ),
        );
      }
    },
    [selectedSection, reportId, details, makeRequest, setComment],
  );

  useEffect(() => {
    if (
      requestState.type !== 'REQUEST_SUCCESS' ||
      !comment ||
      !requestState.data.newComment.success
    ) {
      return;
    }
    const commentId = requestState.data.newComment.id;
    setRequest(requestInit());
    onSuccess(selectedSection, { ...comment, commentId });
    setComment(undefined);
    handleClose();
  }, [
    comment,
    requestState,
    selectedSection,
    handleClose,
    setComment,
    makeRequest,
    setRequest,
  ]);

  if (!open)
    return (
      <StyledButton
        onClick={handleOpen}
        variant="secondary-large"
        className="start-thread-button"
      >
        {buttonText}
      </StyledButton>
    );
  return (
    <StyledDiv>
      <FeedbackSectionTextarea
        id="feedback-text-area"
        autoFocus
        errorMessage=""
        placeholder=""
        initialValue=""
        onCancel={handleClose}
        onSave={handleSave}
      />
    </StyledDiv>
  );
};

export default CommentArea;
